/**
 * Epic Gantt View
 *
 * Timeline visualization of Epics with their start and end dates.
 * Uses shadcn components (no HeroUI).
 * Supports zoom levels: week, month, quarter, year
 */

import { useMemo, useState } from 'react';
import { Epic, EpicStatus } from 'shared/types/epic';
import {
  format,
  differenceInDays,
  addDays,
  startOfMonth,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  CaretLeft,
  CaretRight,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus
} from '@phosphor-icons/react';

interface EpicGanttViewProps {
  epics: Epic[];
  onEpicClick?: (epic: Epic) => void;
}

type ZoomLevel = 'week' | 'month' | 'quarter' | 'year';

export const EpicGanttView = ({ epics, onEpicClick }: EpicGanttViewProps) => {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [viewStartDate, setViewStartDate] = useState<Date>(() => {
    // Find earliest epic start date, or use current month
    const epicWithEarliestDate = epics.find((e) => e.startDate);
    if (epicWithEarliestDate?.startDate) {
      return new Date(epicWithEarliestDate.startDate);
    }
    return startOfMonth(new Date());
  });

  // View settings for different zoom levels
  const viewSettings = useMemo(() => {
    const settings = {
      week: {
        dayWidth: 30,
        daysToShow: 28,
        format: 'dd MMM',
        headerFormat: 'dd MMM yyyy',
        navigateBy: 28
      },
      month: {
        dayWidth: 20,
        daysToShow: 90,
        format: 'dd',
        headerFormat: 'MMMM yyyy',
        navigateBy: 90
      },
      quarter: {
        dayWidth: 10,
        daysToShow: 180,
        format: 'dd MMM',
        headerFormat: 'MMMM yyyy',
        navigateBy: 90
      },
      year: {
        dayWidth: 5,
        daysToShow: 365,
        format: 'MMM',
        headerFormat: 'yyyy',
        navigateBy: 180
      }
    };
    return settings[zoomLevel];
  }, [zoomLevel]);

  // Calculate timeline dates
  const timelineData = useMemo(() => {
    const { daysToShow } = viewSettings;
    const dates = Array.from({ length: daysToShow }, (_, i) =>
      addDays(viewStartDate, i)
    );

    // Group dates by month
    const groups = dates.reduce(
      (acc, date) => {
        const groupKey = format(date, 'yyyy-MM');

        if (!acc[groupKey]) {
          acc[groupKey] = {
            header: format(date, viewSettings.headerFormat, { locale: nl }),
            dates: []
          };
        }
        acc[groupKey].dates.push(date);
        return acc;
      },
      {} as Record<string, { header: string; dates: Date[] }>
    );

    return { dates, groups };
  }, [viewStartDate, viewSettings]);

  // Calculate epic positions and widths
  const epicsData = useMemo(() => {
    const filteredEpics = epics.filter((epic) => {
      return epic.startDate && epic.endDate;
    });

    return filteredEpics.map((epic) => {
      const startDate = new Date(epic.startDate!);
      const endDate = new Date(epic.endDate!);

      const left =
        differenceInDays(startDate, viewStartDate) * viewSettings.dayWidth;
      const width = Math.max(
        (differenceInDays(endDate, startDate) + 1) * viewSettings.dayWidth,
        viewSettings.dayWidth
      );

      // Status colors
      const getStatusColor = (status: EpicStatus) => {
        switch (status) {
          case EpicStatus.PLANNING:
            return { bg: 'bg-blue-500', text: 'text-white' };
          case EpicStatus.IN_PROGRESS:
            return { bg: 'bg-moss-primary', text: 'text-white' };
          case EpicStatus.COMPLETED:
            return { bg: 'bg-status-good', text: 'text-white' };
          case EpicStatus.ON_HOLD:
            return { bg: 'bg-orange-500', text: 'text-white' };
          default:
            return { bg: 'bg-neutral-400', text: 'text-white' };
        }
      };

      const statusColors = getStatusColor(epic.status);

      return {
        ...epic,
        left,
        width,
        startDate,
        endDate,
        statusBg: statusColors.bg,
        statusText: statusColors.text
      };
    });
  }, [epics, viewStartDate, viewSettings.dayWidth]);

  // Navigation handlers
  const handlePrevious = () => {
    setViewStartDate((prev) => addDays(prev, -viewSettings.navigateBy));
  };

  const handleNext = () => {
    setViewStartDate((prev) => addDays(prev, viewSettings.navigateBy));
  };

  const handleZoomIn = () => {
    if (zoomLevel === 'year') setZoomLevel('quarter');
    else if (zoomLevel === 'quarter') setZoomLevel('month');
    else if (zoomLevel === 'month') setZoomLevel('week');
  };

  const handleZoomOut = () => {
    if (zoomLevel === 'week') setZoomLevel('month');
    else if (zoomLevel === 'month') setZoomLevel('quarter');
    else if (zoomLevel === 'quarter') setZoomLevel('year');
  };

  const handleToday = () => {
    setViewStartDate(startOfMonth(new Date()));
  };

  if (epicsData.length === 0) {
    return (
      <Card className="p-8 text-center text-neutral-500">
        <p>Geen epics met datums gevonden</p>
        <p className="text-sm mt-2">
          Voeg start- en einddatums toe aan epics om ze in de timeline te zien
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrevious}>
            <CaretLeft size={20} />
          </Button>
          <Button variant="outline" size="icon" onClick={handleNext}>
            <CaretRight size={20} />
          </Button>
          <Button variant="outline" size="sm" onClick={handleToday}>
            Vandaag
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Select
            value={zoomLevel}
            onValueChange={(value) => setZoomLevel(value as ZoomLevel)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Maand</SelectItem>
              <SelectItem value="quarter">Kwartaal</SelectItem>
              <SelectItem value="year">Jaar</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoomLevel === 'week'}
          >
            <MagnifyingGlassPlus size={20} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoomLevel === 'year'}
          >
            <MagnifyingGlassMinus size={20} />
          </Button>
        </div>
      </div>

      {/* Gantt Chart */}
      <Card className="overflow-x-auto">
        <div className="min-w-max">
          {/* Timeline Header */}
          <div className="border-b bg-neutral-50">
            {/* Month/Year headers */}
            <div className="flex border-b">
              <div className="w-48 flex-shrink-0 border-r p-2 font-semibold text-sm">
                Epic
              </div>
              {Object.values(timelineData.groups).map((group, idx) => (
                <div
                  key={idx}
                  className="border-r p-2 text-center font-semibold text-sm"
                  style={{ width: group.dates.length * viewSettings.dayWidth }}
                >
                  {group.header}
                </div>
              ))}
            </div>

            {/* Day/Week markers */}
            {zoomLevel !== 'year' && (
              <div className="flex text-xs">
                <div className="w-48 flex-shrink-0 border-r" />
                {timelineData.dates.map((date, idx) => (
                  <div
                    key={idx}
                    className="border-r p-1 text-center text-neutral-600"
                    style={{ width: viewSettings.dayWidth }}
                  >
                    {format(date, viewSettings.format, { locale: nl })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Epic Rows */}
          <div className="relative">
            {epicsData.map((epic) => (
              <div
                key={epic.id}
                className="flex border-b hover:bg-neutral-50 transition-colors"
              >
                {/* Epic Name Column */}
                <div className="w-48 flex-shrink-0 border-r p-3">
                  <div
                    className="cursor-pointer hover:text-moss-primary transition-colors"
                    onClick={() => onEpicClick?.(epic as unknown as Epic)}
                  >
                    <div className="font-medium text-sm line-clamp-2">
                      {epic.title}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      EPIC-{epic.id}
                    </div>
                  </div>
                </div>

                {/* Timeline Column */}
                <div className="relative flex-1 p-2">
                  <div
                    className={`absolute top-2 h-8 rounded-md ${epic.statusBg} ${epic.statusText} cursor-pointer hover:opacity-90 transition-opacity shadow-sm flex items-center justify-center text-xs font-medium px-2`}
                    style={{
                      left: `${Math.max(epic.left, 0)}px`,
                      width: `${epic.width}px`
                    }}
                    onClick={() => onEpicClick?.(epic as unknown as Epic)}
                    title={`${epic.title}\n${format(epic.startDate, 'dd MMM yyyy', { locale: nl })} - ${format(epic.endDate, 'dd MMM yyyy', { locale: nl })}`}
                  >
                    <span className="truncate">{epic.title}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
